import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { Table, Typography, Card, Spin, Input, Space, Button, Tooltip } from 'antd';
import { ShopOutlined, SearchOutlined, WalletOutlined, ReloadOutlined, ClockCircleOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import Layout from '../../components/AdminLayout';
import { auth } from '@festpix/services';
import { getBoothWallets, BoothWalletData } from '../../services/adminWalletService';
import { colors } from '@festpix/ui';

const { Title, Text } = Typography;

export default function BoothWalletsPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [boothWallets, setBoothWallets] = useState<BoothWalletData[]>([]);
  const [searchText, setSearchText] = useState('');

  const loadBoothWallets = useCallback(async (isRefresh = false) => {
    if (isRefresh) {
      setRefreshing(true);
    }
    try {
      const data = await getBoothWallets();
      setBoothWallets(data);
      setLastUpdated(new Date());
    } catch (error) {
      console.error('Error loading booth wallets:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!user) {
        navigate('/admin/login');
      } else {
        loadBoothWallets();
      }
    });
    return unsubscribe;
  }, [navigate, loadBoothWallets]);

  const handleRefresh = () => {
    loadBoothWallets(true);
  };

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  };

  const filteredBoothWallets = useMemo(() => {
    if (!searchText.trim()) return boothWallets;
    const search = searchText.toLowerCase();
    return boothWallets.filter(
      (w) =>
        w.boothName?.toLowerCase().includes(search) ||
        w.id.toLowerCase().includes(search) ||
        w.boothId?.toLowerCase().includes(search)
    );
  }, [boothWallets, searchText]);

  const totalBalance = useMemo(() => {
    return boothWallets.reduce((sum, w) => sum + (w.balance || 0), 0);
  }, [boothWallets]);

  const columns: ColumnsType<BoothWalletData> = [
    {
      title: 'Estande',
      dataIndex: 'boothName',
      key: 'boothName',
      render: (name) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <ShopOutlined style={{ color: colors.primary }} />
          <Text strong>{name}</Text>
        </div>
      ),
    },
    {
      title: 'Saldo',
      dataIndex: 'balance',
      key: 'balance',
      render: (balance) => (
        <Text style={{ color: colors.success, fontWeight: 500 }}>
          R$ {balance.toFixed(2)}
        </Text>
      ),
      sorter: (a, b) => a.balance - b.balance,
    },
    {
      title: 'ID da Carteira',
      dataIndex: 'id',
      key: 'id',
      render: (id) => (
        <Text code style={{ fontSize: 12 }}>
          {id.substring(0, 12)}...
        </Text>
      ),
    },
  ];

  if (loading) {
    return (
      <Layout>
        <div style={{ display: 'flex', justifyContent: 'center', padding: 100 }}>
          <Spin size="large" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <Title level={2} style={{ margin: 0 }}>
          <WalletOutlined style={{ marginRight: 12 }} />
          Carteiras dos Estandes
        </Title>
        <Space>
          {lastUpdated && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              <ClockCircleOutlined style={{ marginRight: 4 }} />
              Atualizado às {formatTime(lastUpdated)}
            </Text>
          )}
          <Tooltip title="Atualizar lista de carteiras">
            <Button
              icon={<ReloadOutlined spin={refreshing} />}
              onClick={handleRefresh}
              loading={refreshing}
            >
              Atualizar
            </Button>
          </Tooltip>
          <div style={{
            padding: '8px 16px',
            background: colors.successBg,
            borderRadius: 8,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}>
            <Text style={{ color: colors.text, fontSize: 13 }}>Total:</Text>
            <Text strong style={{ color: colors.success, fontSize: 16 }}>
              R$ {totalBalance.toFixed(2)}
            </Text>
          </div>
        </Space>
      </div>

      <Card style={{ background: colors.cardBackground }}>
        <Space style={{ marginBottom: 16 }}>
          <Input
            placeholder="Buscar por nome do estande ou ID"
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: 300 }}
            allowClear
          />
        </Space>
        <Table
          columns={columns}
          dataSource={filteredBoothWallets}
          rowKey="id"
          pagination={{ pageSize: 10 }}
          locale={{ emptyText: 'Nenhuma carteira de estande encontrada' }}
        />
      </Card>

      <Card style={{ marginTop: 16, background: colors.infoBg }}>
        <Text style={{ color: colors.text }}>
          <strong>Nota:</strong> As carteiras dos estandes recebem automaticamente os valores das cobranças realizadas pelos vendedores.
          Para gerenciar estandes e seus vendedores, acesse a página de{' '}
          <a onClick={() => navigate('/admin/booths')} style={{ color: colors.primary, cursor: 'pointer' }}>
            Estandes
          </a>.
        </Text>
      </Card>
    </Layout>
  );
}
